




























































































































































































































































import {
  defineComponent,
  ref,
  PropType,
  toRefs,
  computed,
  watch
} from '@nuxtjs/composition-api'

import { useI18n } from '~/compositions/i18n'
import CDraggable from '~/components/shared/configurable/CDraggable.vue'
import { defineComponentTranslations } from '~/utils/i18n'
import { ciCross } from '~/icons/source/regular/cross'
import { getVideoInfoAndCoverUrl } from '~/utils/video'
import CImg from '~/components/shared/configurable/image/CImg.vue'
import { useFileUpload } from '~/compositions/files/upload'
import {
  ShortVideo,
  VideoInfo,
  ShortVideosData,
  ShortVideoStatus
} from '~/models/short-video'
import { useDraggable, useDroppable } from '~/compositions/cursor'
import ShortVideoEntry from '~/components/shared/configurable/file/ShortVideoEntry.vue'
import { TranslateResult } from 'vue-i18n'
import ShortVideosPendingThumb from '~/components/shared/short-videos/ShortVideosPendingThumb.vue'
import { useSnackbar } from '~/compositions/snackbar'
import UserAgentService from '~/services/UserAgentService'
import { useDep } from '~/compositions/dependency-container'

export default defineComponent({
  components: {
    CDraggable,
    CImg,
    ShortVideoEntry,
    ShortVideosPendingThumb,
    CVideoPlayer: () =>
      import('~/components/shared/configurable/video/CVideoPlayer.vue'),
    CConfirmationModal: () =>
      import('~/components/shared/configurable/modal/CConfirmationModal.vue')
  },
  props: {
    videos: {
      type: Object as PropType<ShortVideosData>,
      default: null
    },
    acceptedFileTypes: {
      type: Array as PropType<string[]>,
      default() {
        return []
      }
    },
    classifiedId: {
      type: Number,
      default: null
    },
    numberOfFiles: {
      type: Number,
      default: 3
    },
    titleInputPlaceholder: {
      type: String,
      default: null
    },
    showAddButtonAfterUpload: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    videoGalleryLoading: {
      type: Boolean,
      default: false
    },
    progress: {
      type: Number,
      default: 0
    },
    withoutDeleteButton: {
      type: Boolean,
      default: false
    },
    disabledInputMessage: {
      type: String,
      default: ''
    }
  },
  setup(props, { emit }) {
    const {
      acceptedFileTypes,
      numberOfFiles,
      videos,
      loading,
      disabledInputMessage
    } = toRefs(props)
    const { t } = useI18n()
    const { filesAreOfAcceptableType } = useFileUpload()
    const { onDragStart, onDragEnd } = useDraggable()
    const snackbar = useSnackbar()
    const { droppingOver, onDrop, onDragOver, onDragLeave } = useDroppable(
      ref(false),
      triggerInput
    )
    const userAgentService = useDep(UserAgentService)
    const isIos = userAgentService.isIos()

    const isConfirmationModalVisible = ref(false)
    const isModalVisible = ref(false)
    const invalidVideoMessage = ref('')
    const selectedVideo = ref<ShortVideo>()
    const videoDescription = ref('')
    const uploadButtonLoading = ref(false)

    const publicAndPendingVideos = ref<ShortVideo[]>([])
    const hiddenVideos = ref<{
      title: TranslateResult
      subtitle: TranslateResult
      value: ShortVideo[]
    }>({
      title: t('rejected_videos'),
      value: videos.hidden as ShortVideo[],
      subtitle: t('illegal_material')
    })
    const dropInputTemplateRef = ref<HTMLElement | null>(null)
    const title = ref(t('video_gallery'))
    const imageCover = ref('')
    const imagesLoaded = ref<number[]>([])

    watch(
      videos,
      newVal => {
        publicAndPendingVideos.value = [
          newVal?.public as ShortVideo[],
          newVal?.pending as ShortVideo[]
        ]
          .filter(v => v)
          .flat()

        hiddenVideos.value.value = newVal?.hidden as ShortVideo[]
      },
      { immediate: true, deep: true }
    )

    const isUploadingButtonVisible = computed(
      () => (publicAndPendingVideos.value?.length || 0) < numberOfFiles.value
    )

    const publicVideosCount = computed(() => videos.value?.public?.length)

    const numberOfUploadedButtonsPlaceholders = computed(
      () => numberOfFiles.value - publicAndPendingVideos.value?.length
    )

    function handleModalShow() {
      title.value = getTitle()
      videoDescription.value = selectedVideo.value?.description || ''
    }

    function getTitle() {
      return selectedVideo.value ? t('edit_video') : t('upload_video')
    }

    async function validateVideo(files: File[]) {
      const [file] = files
      let videoInfo = null
      try {
        videoInfo = await getVideoInfoAndCoverUrl(file, isIos, 1.5)

        const isVertical = isVideoVertical(videoInfo)
        const isDurationCorrect = isVideoDurationCorrect(videoInfo)
        if (!isVertical) {
          isConfirmationModalVisible.value = true
          invalidVideoMessage.value = t('vertical_video').toString()
          return
        } else if (!isDurationCorrect) {
          isConfirmationModalVisible.value = true
          invalidVideoMessage.value = t('video_duration').toString()
          return
        }

        if (videoInfo && !isVertical && !isDurationCorrect) {
          return
        }
        imageCover.value = videoInfo.coverUrl
        emit('add-file', {
          file,
          duration: videoInfo.duration,
          coverUrl: videoInfo.coverUrl
        })
        isModalVisible.value = true
      } catch (e) {
        snackbar.error(e)
      } finally {
        uploadButtonLoading.value = false
      }
    }

    const hasHiddenVideos = computed(() => hiddenVideos.value?.value?.length)

    const dropContainerClasses = computed({
      get: () => {
        if (droppingOver.value) {
          return ['tw-bg-blue']
        }
        return ['tw-bg-grey-100']
      }
    })

    function isFileTypeAcceptable(files: FileList) {
      if (
        acceptedFileTypes.value?.length &&
        !filesAreOfAcceptableType(files, acceptedFileTypes.value)
      ) {
        return false
      }
      return true
    }

    function fileInputChanged(e: Event, f: FileList[]) {
      uploadButtonLoading.value = true
      const files = (e?.target as any)?.files || f

      if (isFileTypeAcceptable(files)) {
        validateVideo(files)
      } else {
        isConfirmationModalVisible.value = true
        invalidVideoMessage.value = t('wrong_file_type').toString()
        uploadButtonLoading.value = false
      }
    }

    function handleDeleteFile({
      name,
      id,
      file
    }: {
      name: string
      id: string
      file: string
    }) {
      if (!confirm(t('delete_video_confirmation'))) {
        return
      }
      emit('delete', { name, id, file })
    }

    function isVideoVertical(videoInfo: VideoInfo) {
      return videoInfo.width > videoInfo.height
    }
    function isVideoDurationCorrect(videoInfo: VideoInfo) {
      return videoInfo.duration <= 60 && videoInfo.duration >= 5
    }

    function triggerInput(e?: DragEvent): void {
      if (disabledInputMessage.value) {
        snackbar.error(disabledInputMessage.value)
        return
      }
      if (dropInputTemplateRef.value && !e) {
        dropInputTemplateRef.value.click()
      } else {
        fileInputChanged(null, (e.dataTransfer as any).files)
      }
    }

    function handleClickUpload() {
      if (loading.value) {
        return
      }
      if (disabledInputMessage.value) {
        snackbar.error(disabledInputMessage.value)
        return
      }
      triggerInput()
      emit('click')
    }

    function handleImageLoad(videoId: number) {
      imagesLoaded.value.push(videoId)
    }
    function handClickPlay(video: ShortVideo) {
      isModalVisible.value = true
      selectedVideo.value = video
      imageCover.value = video.thumbnail
      emit('play', video)
    }
    function handleClickSave() {
      if (videoDescription.value !== selectedVideo.value?.description) {
        emit('save', {
          video: selectedVideo.value,
          description: videoDescription.value
        })
      }
      isModalVisible.value = false
    }
    function handleCancel() {
      emit('cancel-upload')
      isModalVisible.value = false
    }
    function handleCloseModal() {
      if (loading.value) {
        handleCancel()
      }
      videoDescription.value = ''
    }
    function handleEditVideo(video: ShortVideo) {
      imageCover.value = video.thumbnail
      isModalVisible.value = true
      selectedVideo.value = video
    }

    function handleClickCancel() {
      isModalVisible.value = false
    }

    function closeUploaderModal() {
      isModalVisible.value = false
    }

    return {
      fileInputChanged,
      handleDeleteFile,
      triggerInput,
      dropInputTemplateRef,
      handleClickUpload,
      imageCover,
      publicAndPendingVideos,
      hiddenVideos,
      onDrop,
      onDragOver,
      onDragLeave,
      dropContainerClasses,
      handleImageLoad,
      onDragEnd,
      isUploadingButtonVisible,
      numberOfUploadedButtonsPlaceholders,
      hasHiddenVideos,
      onDragStart,
      ShortVideoStatus,
      publicVideosCount,
      isConfirmationModalVisible,
      invalidVideoMessage,
      isModalVisible,
      handClickPlay,
      title,
      selectedVideo,
      videoDescription,
      handleClickSave,
      handleCancel,
      handleCloseModal,
      handleEditVideo,
      handleModalShow,
      handleClickCancel,
      uploadButtonLoading,
      ciCross,
      closeUploaderModal
    }
  },
  i18n: defineComponentTranslations({
    add_video: {
      en: 'Add video',
      el: 'Προσθήκη βίντεο'
    },

    video_duration: {
      en: 'The duration of the video must be between 5 and 60 seconds.',
      el: 'Η διάρκεια του βίντεο πρέπει να είναι από 5 έως 60 δευτερόλεπτα.'
    },
    vertical_video: {
      en: 'The orientation of the video must be vertical.',
      el: 'Ο προσανατολισμός του βίντεο πρέπει να είναι κάθετος.'
    },
    rejected_videos: {
      en: 'Rejected videos',
      el: 'Απορριφθέντα βίντεο'
    },
    illegal_material: {
      en:
        'There is illegal material in the video content. Replace it with a video related to your classified.',
      el:
        'Υπάρχει μη επιτρεπτό υλικό στο περιεχόμενο του βίντεο. Αντικατάστησέ το με βίντεο που να σχετίζεται με την αγγελία σου.'
    },
    video_preparation: {
      en: 'Video preparation',
      el: 'Προετοιμασία βίντεο'
    },
    unacceptable_video: {
      en: 'Unacceptable video',
      el: 'Μη αποδεκτό βίντεο'
    },
    wrong_file_type: {
      en: 'Wrong file type',
      el: 'Λάθος τύπος αρχείου'
    },
    upload_video: {
      en: 'Upload video',
      el: 'Ανάρτηση βίντεο'
    },
    edit_video: {
      en: 'Edit video',
      el: 'Επεξεργασία βίντεο'
    },
    write_a_title: {
      en: 'Write a title here',
      el: 'Γράψε εδώ έναν τίτλο'
    }
  })
})
